/**
 * Validation utilities for availability blocks
 */

import { extractDatePart } from './timezoneUtils';

/**
 * Get calendar day (YYYY-MM-DD) from a date string without parsing as Date.
 * Avoids timezone-dependent behavior when API returns "2025-01-31T00:00:00" (no Z),
 * which JavaScript would parse as local time (e.g. Kabul UTC+4:30 → previous day in UTC).
 */
function getCalendarDay(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const s = String(dateStr).trim();
  if (s.match(/^\d{4}-\d{2}-\d{2}$/)) return s;
  if (s.includes('T')) return s.split('T')[0];
  if (s.includes(' ')) return s.split(' ')[0];
  return s;
}

export interface TimeBlock {
  start: string;
  end: string;
}

/** Parse "HH:MM" to minutes-since-midnight. */
const hmToMinutes = (hm: string): number => {
  const [h, m] = hm.split(':').map(Number);
  return h * 60 + m;
};

/**
 * Whether two same-day time ranges overlap, honoring midnight crossover.
 * A range whose end < start crosses midnight into the next day.
 * Touching boundaries are NOT considered overlapping.
 */
const timeRangesOverlapWithMidnight = (
  start1: number,
  end1: number,
  start2: number,
  end2: number,
): boolean => {
  const crosses1 = end1 < start1;
  const crosses2 = end2 < start2;

  if (crosses1 && crosses2) {
    // Both blocks cross midnight - they always overlap
    return true;
  }
  if (crosses1) {
    return !(end2 <= start1 && start2 >= end1);
  }
  if (crosses2) {
    return !(end1 <= start2 && start1 >= end2);
  }
  // Neither crosses midnight - standard overlap check
  return !(end1 <= start2 || end2 <= start1);
};

/**
 * Validate time blocks have valid start/end times
 * Supports midnight crossover (e.g., 19:30 - 03:30 is valid for overnight shifts)
 */
export const validateTimeBlocks = (
  timeBlocks: TimeBlock[]
): string | null => {
  for (const block of timeBlocks) {
    const [startHours, startMins] = block.start.split(':').map(Number);
    const [endHours, endMins] = block.end.split(':').map(Number);

    // Validate hours and minutes are valid
    if (isNaN(startHours) || isNaN(startMins) || isNaN(endHours) || isNaN(endMins)) {
      return `Invalid time format in time block: ${block.start} - ${block.end}`;
    }

    if (startHours < 0 || startHours > 23 || startMins < 0 || startMins > 59 ||
        endHours < 0 || endHours > 23 || endMins < 0 || endMins > 59) {
      return `Time values out of range in time block: ${block.start} - ${block.end}`;
    }

    const startMinutes = startHours * 60 + startMins;
    const endMinutes = endHours * 60 + endMins;

    // Allow midnight crossover (end < start means it goes into next day)
    // But ensure the block is not exactly 24 hours (which would be invalid)
    if (startMinutes === endMinutes) {
      return `Start time (${block.start}) cannot equal end time (${block.end}). The time block must have a duration.`;
    }

    // If end < start, it's a valid overnight block (e.g., 19:30 - 03:30)
    // If end > start, it's a valid same-day block (e.g., 09:00 - 17:00)
    // Both are valid, so we don't reject based on this
  }
  return null; // All blocks are valid
};

/**
 * Check if time blocks overlap
 * Handles midnight crossover scenarios (e.g., 19:30 - 03:30)
 */
export const checkTimeBlockOverlap = (
  timeBlocks: TimeBlock[]
): boolean => {
  for (let i = 0; i < timeBlocks.length; i++) {
    for (let j = i + 1; j < timeBlocks.length; j++) {
      const block1 = timeBlocks[i];
      const block2 = timeBlocks[j];

      const overlap = timeRangesOverlapWithMidnight(
        hmToMinutes(block1.start),
        hmToMinutes(block1.end),
        hmToMinutes(block2.start),
        hmToMinutes(block2.end),
      );
      if (overlap) {
        return true; // Overlap found
      }
    }
  }
  return false; // No overlap
};

/**
 * Pure date-range overlap. Treats `null`/missing `to` as +∞.
 * String compare works because dates are YYYY-MM-DD.
 * Touching boundaries count as overlap (an end date IS a working day).
 */
export const dateRangesOverlap = (
  aFrom: string,
  aTo: string | null | undefined,
  bFrom: string,
  bTo: string | null | undefined
): boolean => {
  const aStart = getCalendarDay(aFrom);
  const bStart = getCalendarDay(bFrom);
  if (!aStart || !bStart) return false;
  const aEnd = aTo ? getCalendarDay(aTo) : '9999-12-31';
  const bEnd = bTo ? getCalendarDay(bTo) : '9999-12-31';
  return aStart <= bEnd && bStart <= aEnd;
};

/** Whether this record should be skipped because it's the block currently being edited. */
const recordMatchesExcludedBlock = (
  avail: any,
  excludeBlockKey: string | undefined,
): boolean => {
  if (!excludeBlockKey) return false;
  const effectiveFromDate = extractDatePart(avail.effective_from);
  const effectiveToDate = extractDatePart(avail.effective_to);
  const recordKey = `${effectiveFromDate}_${effectiveToDate || 'ongoing'}`;
  return recordKey === excludeBlockKey;
};

/** Whether any of the new time blocks overlaps the existing record's HH:MM time slot. */
const anyTimeBlockOverlapsSlot = (
  timeBlocks: TimeBlock[],
  existingStartTime: string,
  existingEndTime: string,
): boolean => {
  const existingStart = hmToMinutes(existingStartTime);
  const existingEnd = hmToMinutes(existingEndTime);
  return timeBlocks.some((newBlock) =>
    timeRangesOverlapWithMidnight(
      hmToMinutes(newBlock.start),
      hmToMinutes(newBlock.end),
      existingStart,
      existingEnd,
    ),
  );
};

/**
 * Check if date range and time slots overlap with existing blocks.
 * Caller must pass pre-filtered moderatorAvailabilityRecords (same moderator, same project)
 * so moderator_id matching (id vs user_id vs member_id) is handled in the component.
 */
export const checkDateRangeOverlap = (
  startDate: string,
  endDate: string,
  timeBlocks: TimeBlock[],
  moderatorAvailabilityRecords: any[],
  excludeBlockKey?: string
): boolean => {
  // Build UTC boundaries from calendar day only (no Date parsing of API strings).
  const newStartDateStr = getCalendarDay(startDate);
  const newEndDateStr = getCalendarDay(endDate || startDate);
  if (!newStartDateStr || !newEndDateStr) return false;
  const newStart = new Date(newStartDateStr + 'T00:00:00.000Z');
  const newEnd = new Date(newEndDateStr + 'T23:59:59.999Z');

  // Check each individual record for overlap (records already filtered by moderator in component)
  for (const avail of moderatorAvailabilityRecords) {
    // Skip records from the block we're editing
    // IMPORTANT: Use extractDatePart to match how blockKey is constructed in TeamAvailability.tsx
    if (recordMatchesExcludedBlock(avail, excludeBlockKey)) {
      continue;
    }

    // Explicit: block any new availability starting on an existing slot's end date (last day)
    const existingEndDateStr = avail.effective_to ? getCalendarDay(avail.effective_to) : '';
    if (existingEndDateStr && newStartDateStr === existingEndDateStr) {
      return true; // Overlap - new slot starts on last day of existing slot
    }

    const existingStartDateStr = getCalendarDay(avail.effective_from);
    const existingEndDateStrForRange = getCalendarDay(avail.effective_to || avail.effective_from);
    if (!existingStartDateStr || !existingEndDateStrForRange) continue;
    const existingStart = new Date(existingStartDateStr + 'T00:00:00.000Z');
    const existingEnd = new Date(existingEndDateStrForRange + 'T23:59:59.999Z');

    // Date ranges overlap, now check if time slots overlap
    const dateRangesOverlap = !(newEnd < existingStart || newStart > existingEnd);
    if (!dateRangesOverlap) continue;

    const existingStartTime = avail.start_time.split(':').slice(0, 2).join(':');
    const existingEndTime = avail.end_time.split(':').slice(0, 2).join(':');
    if (anyTimeBlockOverlapsSlot(timeBlocks, existingStartTime, existingEndTime)) {
      return true; // Time slot overlap found
    }
  }

  return false; // No overlap
};

